// src/shared/components/Modals/PickupTimeModal.jsx
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  TextField,
  Alert,
} from "@mui/material";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

// ── Boundary helpers ──────────────────────────────────────────────────────────
// Incoming props may be unix millis (number), ISO string, or Firestore Timestamp.
// Internally the modal always works with ISO strings (datetime inputs need them).
// onConfirm emits unix millis so the parent can store them directly.

const toISO = (ts) => {
  if (!ts) return "";
  try {
    if (ts?.toDate) return ts.toDate().toISOString();           // Firestore Timestamp
    if (typeof ts === "number") return new Date(ts).toISOString(); // unix millis
    const d = new Date(ts);
    return isNaN(d.getTime()) ? "" : d.toISOString();           // ISO string
  } catch { return ""; }
};

const toMillis = (isoString) => {
  if (!isoString) return null;
  const ms = new Date(isoString).getTime();
  return isNaN(ms) ? null : ms;
};

const formatDatetimeLocal = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getMinDatetimeLocal = () => formatDatetimeLocal(new Date().toISOString());

// Default times as ISO strings
const defaultPickupISO = () => new Date(Date.now() + 30 * 60000).toISOString();
const defaultTargetISO = () => new Date(Date.now() + 3 * 60 * 60000).toISOString();
const defaultLatestPickUpISO = () => new Date(Date.now() + 90 * 60000).toISOString();
const defaultEarliestTargetISO = () => new Date(Date.now() + 150 * 60000).toISOString();
// ─────────────────────────────────────────────────────────────────────────────

export function PickupTimeModal({
  open,
  onClose,
  // Props accepted as unix millis, ISO string, or Firestore Timestamp
  currentPickupTime = "",
  currentTargetTime = "",
  currentLatestPickUpTime = "",
  currentEarliestTarget = "",
  hasTimeWindow = false,
  onConfirm,
}) {
  const { t } = useTranslation();

  // Internal state: always ISO strings
  const [pickupTime, setPickupTime] = useState("");
  const [targetTime, setTargetTime] = useState("");
  const [latestPickUpTime, setLatestPickUpTime] = useState("");
  const [earliestTargetTime, setEarliestTargetTime] = useState("");
  const [showTimeWindow, setShowTimeWindow] = useState(hasTimeWindow);

  useEffect(() => {
    if (open) {
      setPickupTime(toISO(currentPickupTime) || defaultPickupISO());
      setTargetTime(toISO(currentTargetTime) || defaultTargetISO());
      setLatestPickUpTime(toISO(currentLatestPickUpTime) || defaultLatestPickUpISO());
      setEarliestTargetTime(toISO(currentEarliestTarget) || defaultEarliestTargetISO());
      setShowTimeWindow(hasTimeWindow);
    }
  }, [open, currentPickupTime, currentTargetTime, currentLatestPickUpTime, currentEarliestTarget, hasTimeWindow]);

  const handleConfirm = () => {
    // Emit unix millis — parent stores them directly in Firestore
    onConfirm?.({
      pickupTime: toMillis(pickupTime),
      targetTime: toMillis(targetTime),
      latestPickUpTime: showTimeWindow ? toMillis(latestPickUpTime) : null,
      earliestTargetTime: showTimeWindow ? toMillis(earliestTargetTime) : null,
      hasTimeWindow: showTimeWindow,
    });
    onClose();
  };

  const toggleTimeWindow = () => {
    const next = !showTimeWindow;
    setShowTimeWindow(next);
    if (next) {
      if (!latestPickUpTime && pickupTime) {
        setLatestPickUpTime(new Date(new Date(pickupTime).getTime() + 60 * 60000).toISOString());
      }
      if (!earliestTargetTime && targetTime) {
        setEarliestTargetTime(new Date(new Date(targetTime).getTime() - 30 * 60000).toISOString());
      }
    }
  };

  const canConfirm = pickupTime && targetTime &&
    (!showTimeWindow || (latestPickUpTime && earliestTargetTime));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Clock size={20} />
          <Typography variant="h6" fontWeight={700}>
            {t("delivery.time") || "Set Pickup & Delivery Times"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>

          {/* Pickup time(s) */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: showTimeWindow ? "1 1 calc(50% - 8px)" : "1 1 100%", minWidth: 200 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                {showTimeWindow
                  ? (t("delivery.earliestPickupTime") || "Earliest Pickup")
                  : (t("delivery.pickupTime") || "Pickup Time")}
              </Typography>
              <TextField
                fullWidth
                type="datetime-local"
                value={formatDatetimeLocal(pickupTime)}
                onChange={(e) => setPickupTime(new Date(e.target.value).toISOString())}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: getMinDatetimeLocal() }}
              />
            </Box>

            {showTimeWindow && (
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: 200 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  {t("delivery.latestPickupTime") || "Latest Pickup"}
                </Typography>
                <TextField
                  fullWidth
                  type="datetime-local"
                  value={formatDatetimeLocal(latestPickUpTime)}
                  onChange={(e) => setLatestPickUpTime(new Date(e.target.value).toISOString())}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: pickupTime ? formatDatetimeLocal(pickupTime) : getMinDatetimeLocal() }}
                />
              </Box>
            )}
          </Box>

          {/* Target time(s) */}
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {showTimeWindow && (
              <Box sx={{ flex: "1 1 calc(50% - 8px)", minWidth: 200 }}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                  {t("delivery.earliestTargetTime") || "Earliest Delivery"}
                </Typography>
                <TextField
                  fullWidth
                  type="datetime-local"
                  value={formatDatetimeLocal(earliestTargetTime)}
                  onChange={(e) => setEarliestTargetTime(new Date(e.target.value).toISOString())}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: targetTime ? formatDatetimeLocal(targetTime) : undefined }}
                />
              </Box>
            )}

            <Box sx={{ flex: showTimeWindow ? "1 1 calc(50% - 8px)" : "1 1 100%", minWidth: 200 }}>
              <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
                {showTimeWindow
                  ? (t("delivery.latestTargetTime") || "Latest Delivery")
                  : (t("delivery.targetTime") || "Target Delivery Time")}
              </Typography>
              <TextField
                fullWidth
                type="datetime-local"
                value={formatDatetimeLocal(targetTime)}
                onChange={(e) => setTargetTime(new Date(e.target.value).toISOString())}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: pickupTime ? formatDatetimeLocal(pickupTime) : getMinDatetimeLocal() }}
              />
            </Box>
          </Box>

          {/* Time window toggle + price note */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Button
              variant={showTimeWindow ? "contained" : "outlined"}
              onClick={toggleTimeWindow}
              sx={{ flexShrink: 0 }}
            >
              {showTimeWindow
                ? (t("delivery.hideTimeWindow") || "Hide Time Window")
                : (t("delivery.setTimeWindow") || "Set Time Window")}
            </Button>

            {showTimeWindow && (
              <Alert severity="info" icon={false} sx={{ py: 0.5, fontSize: "0.875rem", flex: 1 }}>
                {t("delivery.timeWindowPriceNote") || "Defining a time window will add 4€ + VAT to the delivery price"}
              </Alert>
            )}
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>
          {t("common.cancel") || "Cancel"}
        </Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!canConfirm}>
          {t("common.confirm") || "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}